import { useState, useEffect, useCallback } from 'react';

const useNewsRefresh = (fetchFunction, dependencies = [], storageKey = 'newsLastRefreshTime') => {
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [canRefresh, setCanRefresh] = useState(true);
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(0);

  // Check if we can refresh based on localStorage
  const checkRefreshAvailability = useCallback(() => {
    const storedTime = localStorage.getItem(storageKey);
    if (storedTime) {
      const lastRefresh = new Date(storedTime).getTime();
      const now = new Date().getTime();
      const timeDiff = now - lastRefresh;
      const oneMinute = 60 * 1000; // 60 seconds in milliseconds
      
      if (timeDiff < oneMinute) {
        const remainingTime = Math.ceil((oneMinute - timeDiff) / 1000);
        setTimeUntilRefresh(remainingTime);
        setCanRefresh(false);
        return false;
      }
    }
    setCanRefresh(true);
    setTimeUntilRefresh(0);
    return true;
  }, [storageKey]);

  // Initialize on mount
  useEffect(() => {
    checkRefreshAvailability();
  }, [checkRefreshAvailability]);

  // Update countdown timer
  useEffect(() => {
    if (!canRefresh && timeUntilRefresh > 0) {
      const timer = setInterval(() => {
        setTimeUntilRefresh(prev => {
          if (prev <= 1) {
            setCanRefresh(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [canRefresh, timeUntilRefresh]);

  // Refresh function with cooldown
  const refreshNews = useCallback(async () => {
    if (!canRefresh) {
      console.log(`News refresh blocked. Please wait ${timeUntilRefresh} seconds.`);
      return false;
    }

    try {
      await fetchFunction();
      
      // Update localStorage with current timestamp
      const now = new Date().toISOString();
      localStorage.setItem(storageKey, now);
      setLastRefreshTime(now);
      
      // Reset refresh state
      setCanRefresh(false);
      setTimeUntilRefresh(60); // Start 60-second countdown
      
      console.log('News refreshed successfully');
      return true;
    } catch (error) {
      console.error('Failed to refresh news:', error);
      return false;
    }
  }, [canRefresh, timeUntilRefresh, fetchFunction]);

  // Force refresh (bypasses cooldown - for development/testing)
  const forceRefresh = useCallback(async () => {
    try {
      await fetchFunction();
      console.log('News force refreshed successfully');
      return true;
    } catch (error) {
      console.error('Failed to force refresh news:', error);
      return false;
    }
  }, [fetchFunction]);

  return {
    refreshNews,
    forceRefresh,
    canRefresh,
    timeUntilRefresh,
    lastRefreshTime
  };
};

export default useNewsRefresh;
